import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import DriveFilePreview from '../components/DriveFilePreview';
import SearchableDropdown from '../components/SearchableDropdown';
import { useAuth } from '../context/AuthContext';
import { fetchVerifiedUsers, VerifiedUser, ROLE_OPTIONS } from '../api/users';
import { ROLE_LABELS } from '../constants/taskTags';
import { SessionExpiredError } from '../api/session';
import {
  archiveDriveFile,
  archiveDriveFolder,
  createDriveFolder,
  downloadDriveFile,
  fetchArchivedDriveItems,
  fetchDriveItems,
  fetchFolderOptions,
  fetchFolderShares,
  fetchSharedFolders,
  formatBytes,
  removeFolderRoleShare,
  removeFolderShare,
  restoreDriveFile,
  restoreDriveFolder,
  shareDriveFolder,
  stopSharingFolder,
  updateDriveFile,
  updateDriveFolder,
  updateFolderRoleShare,
  updateFolderShare,
  uploadDriveFile,
  ArchivedDriveItem,
  DriveFile,
  DriveFolder,
  DriveListing,
  FolderOption,
  FolderRoleShare,
  FolderShare,
  SharedByMeFolder,
  SharedFolder,
  SharePermission,
} from '../api/fileDrive';
import { readDroppedFiles } from '../utils/readDroppedFiles';
import './Page.css';
import './FileDrive.css';

type TabId = 'personal' | 'shared' | 'archived';

interface UploadItem {
  id: string;
  name: string;
  size: number;
  loaded: number;
  status: 'queued' | 'uploading' | 'done' | 'error' | 'cancelled';
  error?: string;
}

interface QueuedUpload {
  id: string;
  file: File;
  folderId: number | null;
}

type RenameTarget = { kind: 'folder' | 'file'; id: number; name: string };
type ArchiveTarget = RenameTarget;
type MoveTarget = RenameTarget & { currentFolderId: number | null };

let uploadCounter = 0;
function nextUploadId(): string {
  uploadCounter += 1;
  return `u${Date.now()}-${uploadCounter}`;
}

function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role.replace(/_/g, ' ');
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const FolderIcon: React.FC = () => (
  <svg className="drive-icon drive-icon--folder" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h4.2c.5 0 .96.24 1.24.65l.86 1.25h8.7A1.5 1.5 0 0 1 21 8.4v9.1a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5Z" />
  </svg>
);

const FileIcon: React.FC = () => (
  <svg className="drive-icon drive-icon--file" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 3.5h7L19 9.5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-16a1 1 0 0 1 1-1Z" />
    <path className="drive-icon-fold" d="M13 3.5 19 9.5h-6Z" />
  </svg>
);

const FileDrive: React.FC = () => {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab: TabId = tabParam === 'shared' || tabParam === 'archived' ? tabParam : 'personal';
  const folderParam = searchParams.get('folder');
  const openFolderId = folderParam && /^\d+$/.test(folderParam) ? Number(folderParam) : null;

  const [listing, setListing] = useState<DriveListing | null>(null);
  const [sharedFolders, setSharedFolders] = useState<SharedFolder[]>([]);
  const [sharedByMe, setSharedByMe] = useState<SharedByMeFolder[]>([]);
  const [archivedItems, setArchivedItems] = useState<ArchivedDriveItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [uploadsCollapsed, setUploadsCollapsed] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);
  const queueRef = useRef<QueuedUpload[]>([]);
  const drainingRef = useRef(false);
  const cancelledRef = useRef<Set<string>>(new Set());
  const activeCancelRef = useRef<{ id: string; cancel: () => void } | null>(null);

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  /** Set when the folder is being created from the Shared tab, so sharing opens straight after. */
  const [shareAfterCreate, setShareAfterCreate] = useState(false);
  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<ArchivedDriveItem | null>(null);
  const [previewFile, setPreviewFile] = useState<DriveFile | null>(null);
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
  const [moveOptions, setMoveOptions] = useState<FolderOption[]>([]);
  const [moveDestination, setMoveDestination] = useState<string>('');
  const [shareTarget, setShareTarget] = useState<{ id: number; name: string } | null>(null);
  const [shareUsers, setShareUsers] = useState<VerifiedUser[]>([]);
  const [shareSelection, setShareSelection] = useState<number[]>([]);
  const [shareRoleSelection, setShareRoleSelection] = useState<string[]>([]);
  const [sharePermission, setSharePermission] = useState<SharePermission>('view');
  const [shareAddField, setShareAddField] = useState('');
  const [shareRoleField, setShareRoleField] = useState('');
  const [existingShares, setExistingShares] = useState<FolderShare[]>([]);
  const [existingRoleShares, setExistingRoleShares] = useState<FolderRoleShare[]>([]);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  /* The folder picker needs a non-standard attribute React will not render for us. */
  useEffect(() => {
    if (folderInputRef.current) folderInputRef.current.setAttribute('webkitdirectory', '');
  }, []);

  const handleApiError = useCallback((err: unknown, fallback: string): string => {
    if (err instanceof SessionExpiredError) {
      setSessionExpired(true);
      return err.message;
    }
    return err instanceof Error ? err.message : fallback;
  }, []);

  const reload = useCallback(async () => {
    if (!token) {
      setSessionExpired(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'archived') {
        setArchivedItems(await fetchArchivedDriveItems());
        setListing(null);
      } else if (activeTab === 'shared' && openFolderId == null) {
        const shared = await fetchSharedFolders();
        setSharedFolders(shared.folders);
        setSharedByMe(shared.sharedByMe);
        setListing(null);
      } else {
        setListing(await fetchDriveItems(openFolderId));
      }
    } catch (err) {
      setError(handleApiError(err, 'Failed to load the drive.'));
    } finally {
      setLoading(false);
    }
  }, [activeTab, openFolderId, token, handleApiError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const selectTab = useCallback(
    (tab: TabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          next.delete('folder');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const openFolder = useCallback(
    (id: number | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', activeTab);
          if (id == null) next.delete('folder');
          else next.set('folder', String(id));
          return next;
        },
        { replace: true }
      );
    },
    [activeTab, setSearchParams]
  );

  const permission = listing?.permission ?? 'owner';
  const canEdit = openFolderId == null ? activeTab === 'personal' : permission === 'owner' || permission === 'edit';

  /* ----------------------------- uploads ----------------------------- */

  const patchUpload = useCallback((id: string, updates: Partial<UploadItem>) => {
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...updates } : u)));
  }, []);

  const drainQueue = useCallback(async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const next = queueRef.current.shift()!;
        if (cancelledRef.current.has(next.id)) {
          cancelledRef.current.delete(next.id);
          patchUpload(next.id, { status: 'cancelled' });
          continue;
        }
        patchUpload(next.id, { status: 'uploading' });
        const handle = uploadDriveFile(next.file, next.folderId, (loaded) => {
          patchUpload(next.id, { loaded });
        });
        activeCancelRef.current = { id: next.id, cancel: handle.cancel };
        try {
          await handle.promise;
          patchUpload(next.id, { status: 'done', loaded: next.file.size });
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') {
            patchUpload(next.id, { status: 'cancelled' });
          } else {
            patchUpload(next.id, {
              status: 'error',
              error: handleApiError(err, 'Upload failed.'),
            });
          }
        } finally {
          activeCancelRef.current = null;
        }
      }
    } finally {
      drainingRef.current = false;
      await reload();
    }
  }, [patchUpload, reload, handleApiError]);

  const enqueueUploads = useCallback(
    (items: { file: File; folderId: number | null }[]) => {
      if (items.length === 0) return;
      const queued: QueuedUpload[] = items.map((item) => ({
        id: nextUploadId(),
        file: item.file,
        folderId: item.folderId,
      }));
      setUploads((prev) => [
        ...prev,
        ...queued.map((q) => ({
          id: q.id,
          name: q.file.name,
          size: q.file.size,
          loaded: 0,
          status: 'queued' as const,
        })),
      ]);
      setUploadsCollapsed(false);
      queueRef.current.push(...queued);
      void drainQueue();
    },
    [drainQueue]
  );

  const cancelUpload = useCallback((id: string) => {
    if (activeCancelRef.current?.id === id) {
      activeCancelRef.current.cancel();
      return;
    }
    cancelledRef.current.add(id);
    queueRef.current = queueRef.current.filter((q) => q.id !== id);
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status: 'cancelled' } : u)));
  }, []);

  /**
   * Creates the folders a dropped directory tree needs, so the drive mirrors what
   * the person dragged in. Paths already created during this drop are reused.
   */
  const ensureFolderPath = useCallback(
    async (path: string[], baseFolderId: number | null, cache: Map<string, number>): Promise<number | null> => {
      let parent = baseFolderId;
      const walked: string[] = [];
      for (const segment of path) {
        walked.push(segment);
        const key = walked.join('/');
        const cached = cache.get(key);
        if (cached != null) {
          parent = cached;
          continue;
        }
        const created = await createDriveFolder(segment, parent);
        cache.set(key, created.id);
        parent = created.id;
      }
      return parent;
    },
    []
  );

  const startUploads = useCallback(
    async (dropped: { file: File; path: string[] }[]) => {
      if (dropped.length === 0) return;
      if (!canEdit) {
        setNotice('You only have view access here, so you cannot add files.');
        return;
      }
      const cache = new Map<string, number>();
      const items: { file: File; folderId: number | null }[] = [];
      try {
        for (const entry of dropped) {
          const destination =
            entry.path.length > 0 ? await ensureFolderPath(entry.path, openFolderId, cache) : openFolderId;
          items.push({ file: entry.file, folderId: destination });
        }
      } catch (err) {
        setError(handleApiError(err, 'Failed to create the dropped folders.'));
      }
      enqueueUploads(items);
      if (cache.size > 0) await reload();
    },
    [canEdit, ensureFolderPath, openFolderId, enqueueUploads, reload, handleApiError]
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      dragDepth.current = 0;
      setDragActive(false);
      if (activeTab === 'archived') return;
      const dropped = await readDroppedFiles(event.dataTransfer);
      await startUploads(dropped);
    },
    [activeTab, startUploads]
  );

  /* The Archived tab is a place to look at what you put away, so a drop there
     would land somewhere the user isn't looking. It takes no uploads. */
  const handleDragEnter = useCallback(
    (event: React.DragEvent) => {
      if (activeTab === 'archived') return;
      if (!Array.from(event.dataTransfer.types || []).includes('Files')) return;
      dragDepth.current += 1;
      setDragActive(true);
    },
    [activeTab]
  );

  const handleDragLeave = useCallback(() => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragActive(false);
  }, []);

  const handleFilesPicked = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      // A folder picked through the dialog carries its path on each file.
      const dropped = Array.from(fileList).map((file) => {
        const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath || '';
        const segments = relative ? relative.split('/').slice(0, -1) : [];
        return { file, path: segments };
      });
      await startUploads(dropped);
    },
    [startUploads]
  );

  const activeUploads = uploads.filter((u) => u.status === 'queued' || u.status === 'uploading');
  const uploadTotals = useMemo(() => {
    const total = uploads.reduce((sum, u) => sum + u.size, 0);
    const loaded = uploads.reduce((sum, u) => sum + (u.status === 'done' ? u.size : u.loaded), 0);
    return { total, loaded, percent: total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0 };
  }, [uploads]);

  /* ----------------------------- actions ----------------------------- */

  const openShare = useCallback(
    async (folder: { id: number; name: string }) => {
      setShareTarget(folder);
      setShareSelection([]);
      setShareRoleSelection([]);
      setSharePermission('view');
      setShareAddField('');
      setShareRoleField('');
      setExistingShares([]);
      setExistingRoleShares([]);
      setModalError(null);
      try {
        const [users, shares] = await Promise.all([fetchVerifiedUsers(), fetchFolderShares(folder.id)]);
        setShareUsers(users.filter((u) => u.id !== user?.id));
        setExistingShares(shares.shares ?? []);
        setExistingRoleShares(shares.roleShares ?? []);
      } catch (err) {
        setModalError(handleApiError(err, 'Failed to load sharing.'));
      }
    },
    [user?.id, handleApiError]
  );

  const submitNewFolder = useCallback(async () => {
    const name = newFolderName.trim();
    if (!name) return;
    setBusy(true);
    setModalError(null);
    try {
      // Started from the Shared tab, the folder still belongs in the owner's own
      // drive; it only becomes shared once people or roles are added to it.
      const created = await createDriveFolder(name, shareAfterCreate ? null : openFolderId);
      setNewFolderOpen(false);
      setNewFolderName('');
      await reload();
      if (shareAfterCreate) {
        setShareAfterCreate(false);
        await openShare({ id: created.id, name: created.name });
      }
    } catch (err) {
      setModalError(handleApiError(err, 'Failed to create the folder.'));
    } finally {
      setBusy(false);
    }
  }, [newFolderName, openFolderId, shareAfterCreate, reload, openShare, handleApiError]);

  const submitRename = useCallback(async () => {
    if (!renameTarget) return;
    const name = renameValue.trim();
    if (!name) return;
    setBusy(true);
    setModalError(null);
    try {
      if (renameTarget.kind === 'folder') await updateDriveFolder(renameTarget.id, { name });
      else await updateDriveFile(renameTarget.id, { name });
      setRenameTarget(null);
      await reload();
    } catch (err) {
      setModalError(handleApiError(err, 'Failed to rename.'));
    } finally {
      setBusy(false);
    }
  }, [renameTarget, renameValue, reload, handleApiError]);

  const submitArchive = useCallback(async () => {
    if (!archiveTarget) return;
    setBusy(true);
    setModalError(null);
    try {
      if (archiveTarget.kind === 'folder') await archiveDriveFolder(archiveTarget.id);
      else await archiveDriveFile(archiveTarget.id);
      const name = archiveTarget.name;
      setArchiveTarget(null);
      await reload();
      setNotice(`${name} was moved to Archived. You can restore it from the Archived tab.`);
    } catch (err) {
      setModalError(handleApiError(err, 'Failed to archive.'));
    } finally {
      setBusy(false);
    }
  }, [archiveTarget, reload, handleApiError]);

  const submitRestore = useCallback(async () => {
    if (!restoreTarget) return;
    setBusy(true);
    setModalError(null);
    try {
      if (restoreTarget.kind === 'folder') await restoreDriveFolder(restoreTarget.id);
      else await restoreDriveFile(restoreTarget.id);
      const { name, location } = restoreTarget;
      setRestoreTarget(null);
      await reload();
      setNotice(`${name} is back in ${location ?? 'My Drive'}.`);
    } catch (err) {
      setModalError(handleApiError(err, 'Failed to restore.'));
    } finally {
      setBusy(false);
    }
  }, [restoreTarget, reload, handleApiError]);

  const openMove = useCallback(
    async (target: MoveTarget) => {
      setMoveTarget(target);
      setMoveDestination('');
      setModalError(null);
      try {
        setMoveOptions(await fetchFolderOptions());
      } catch (err) {
        setModalError(handleApiError(err, 'Failed to load folders.'));
      }
    },
    [handleApiError]
  );

  /** Full path labels so two folders with the same name are still distinguishable. */
  const moveChoices = useMemo(() => {
    if (!moveTarget) return [] as { label: string; folderId: number | null }[];
    const byId = new Map(moveOptions.map((f) => [f.id, f]));
    const labelFor = (folder: FolderOption): string => {
      const parts: string[] = [folder.name];
      let cursor = folder.parentFolderId;
      let guard = 0;
      while (cursor != null && guard < 50) {
        const parent = byId.get(cursor);
        if (!parent) break;
        parts.unshift(parent.name);
        cursor = parent.parentFolderId;
        guard += 1;
      }
      const prefix = folder.ownerUserId === user?.id ? 'My Drive' : `${folder.ownerUsername}'s drive`;
      return `${prefix} / ${parts.join(' / ')}`;
    };
    const descendantsOfMoved = new Set<number>();
    if (moveTarget.kind === 'folder') {
      const collect = (parentId: number) => {
        for (const option of moveOptions) {
          if (option.parentFolderId === parentId && !descendantsOfMoved.has(option.id)) {
            descendantsOfMoved.add(option.id);
            collect(option.id);
          }
        }
      };
      descendantsOfMoved.add(moveTarget.id);
      collect(moveTarget.id);
    }
    // The top level of a drive is private, so it is only a destination for things
    // already in your own drive.
    const choices: { label: string; folderId: number | null }[] =
      permission === 'owner' ? [{ label: 'My Drive (top level)', folderId: null }] : [];
    for (const option of moveOptions) {
      if (descendantsOfMoved.has(option.id)) continue;
      if (option.id === moveTarget.currentFolderId) continue;
      // Only the owner can restructure folders, so folders can only move within their own drive.
      if (moveTarget.kind === 'folder' && option.ownerUserId !== user?.id) continue;
      choices.push({ label: labelFor(option), folderId: option.id });
    }
    return choices;
  }, [moveOptions, moveTarget, permission, user?.id]);

  const submitMove = useCallback(async () => {
    if (!moveTarget || !moveDestination) return;
    const choice = moveChoices.find((c) => c.label === moveDestination);
    if (!choice) {
      setModalError('Pick a destination from the list.');
      return;
    }
    setBusy(true);
    setModalError(null);
    try {
      if (moveTarget.kind === 'folder') {
        await updateDriveFolder(moveTarget.id, { parentFolderId: choice.folderId });
      } else {
        await updateDriveFile(moveTarget.id, { folderId: choice.folderId });
      }
      setMoveTarget(null);
      await reload();
    } catch (err) {
      setModalError(handleApiError(err, 'Failed to move.'));
    } finally {
      setBusy(false);
    }
  }, [moveTarget, moveDestination, moveChoices, reload, handleApiError]);

  const submitShare = useCallback(async () => {
    if (!shareTarget || (shareSelection.length === 0 && shareRoleSelection.length === 0)) return;
    setBusy(true);
    setModalError(null);
    try {
      const result = await shareDriveFolder(shareTarget.id, {
        userIds: shareSelection,
        roles: shareRoleSelection,
        permission: sharePermission,
      });
      setExistingShares(result.shares ?? []);
      setExistingRoleShares(result.roleShares ?? []);
      setShareSelection([]);
      setShareRoleSelection([]);
      setNotice(
        result.notified > 0
          ? `Shared “${shareTarget.name}” and emailed ${result.notified} ${result.notified === 1 ? 'person' : 'people'}.`
          : `Updated sharing on “${shareTarget.name}”.`
      );
      await reload();
    } catch (err) {
      setModalError(handleApiError(err, 'Failed to share the folder.'));
    } finally {
      setBusy(false);
    }
  }, [shareTarget, shareSelection, shareRoleSelection, sharePermission, reload, handleApiError]);

  const changeSharePermission = useCallback(
    async (userId: number, next: SharePermission) => {
      if (!shareTarget) return;
      setModalError(null);
      try {
        await updateFolderShare(shareTarget.id, userId, next);
        setExistingShares((prev) => prev.map((s) => (s.userId === userId ? { ...s, permission: next } : s)));
      } catch (err) {
        setModalError(handleApiError(err, 'Failed to update sharing.'));
      }
    },
    [shareTarget, handleApiError]
  );

  const revokeShare = useCallback(
    async (userId: number) => {
      if (!shareTarget) return;
      setModalError(null);
      try {
        await removeFolderShare(shareTarget.id, userId);
        setExistingShares((prev) => prev.filter((s) => s.userId !== userId));
        await reload();
      } catch (err) {
        setModalError(handleApiError(err, 'Failed to update sharing.'));
      }
    },
    [shareTarget, reload, handleApiError]
  );

  const changeRoleSharePermission = useCallback(
    async (role: string, next: SharePermission) => {
      if (!shareTarget) return;
      setModalError(null);
      try {
        await updateFolderRoleShare(shareTarget.id, role, next);
        setExistingRoleShares((prev) => prev.map((s) => (s.role === role ? { ...s, permission: next } : s)));
      } catch (err) {
        setModalError(handleApiError(err, 'Failed to update sharing.'));
      }
    },
    [shareTarget, handleApiError]
  );

  const revokeRoleShare = useCallback(
    async (role: string) => {
      if (!shareTarget) return;
      setModalError(null);
      try {
        await removeFolderRoleShare(shareTarget.id, role);
        setExistingRoleShares((prev) => prev.filter((s) => s.role !== role));
        await reload();
      } catch (err) {
        setModalError(handleApiError(err, 'Failed to update sharing.'));
      }
    },
    [shareTarget, reload, handleApiError]
  );

  const makeFolderPrivate = useCallback(
    async (folderId: number, folderName: string) => {
      setBusy(true);
      setModalError(null);
      try {
        await stopSharingFolder(folderId);
        setExistingShares([]);
        setExistingRoleShares([]);
        setNotice(`“${folderName}” is private again.`);
        await reload();
      } catch (err) {
        const message = handleApiError(err, 'Failed to update sharing.');
        if (shareTarget) setModalError(message);
        else setError(message);
      } finally {
        setBusy(false);
      }
    },
    [reload, handleApiError, shareTarget]
  );

  const leaveSharedFolder = useCallback(
    async (folderId: number) => {
      if (!user) return;
      try {
        await removeFolderShare(folderId, user.id);
        await reload();
      } catch (err) {
        setError(handleApiError(err, 'Failed to remove the folder.'));
      }
    },
    [user, reload, handleApiError]
  );

  const download = useCallback(
    async (fileId: number) => {
      try {
        await downloadDriveFile(fileId);
      } catch (err) {
        setError(handleApiError(err, 'Failed to download the file.'));
      }
    },
    [handleApiError]
  );

  /* ----------------------------- rendering ----------------------------- */

  const canActOnFile = useCallback(
    (file: DriveFile) => permission === 'owner' || (permission === 'edit' && file.uploadedByUserId === user?.id),
    [permission, user?.id]
  );

  const renderRows = (data: DriveListing) => {
    if (data.folders.length === 0 && data.files.length === 0) {
      return (
        <tr>
          <td colSpan={5} className="drive-empty-cell">
            {canEdit
              ? 'Nothing here yet. Drag files or folders onto this page to upload them.'
              : 'This folder is empty.'}
          </td>
        </tr>
      );
    }
    return (
      <>
        {data.folders.map((folder: DriveFolder) => (
          <tr key={`folder-${folder.id}`}>
            <td>
              <button type="button" className="drive-name-button" onClick={() => openFolder(folder.id)}>
                <FolderIcon />
                <span className="drive-name-text">{folder.name}</span>
              </button>
              {folder.shareCount ? <span className="drive-pill">Shared</span> : null}
            </td>
            <td className="drive-cell-muted">Folder</td>
            <td className="drive-cell-muted">—</td>
            <td className="drive-cell-muted">{formatWhen(folder.updatedAt)}</td>
            <td>
              <div className="drive-row-actions">
                {permission === 'owner' ? (
                  <>
                    <button type="button" className="drive-action" onClick={() => openShare({ id: folder.id, name: folder.name })}>
                      Share
                    </button>
                    <button
                      type="button"
                      className="drive-action"
                      onClick={() => {
                        setRenameTarget({ kind: 'folder', id: folder.id, name: folder.name });
                        setRenameValue(folder.name);
                        setModalError(null);
                      }}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="drive-action"
                      onClick={() =>
                        void openMove({
                          kind: 'folder',
                          id: folder.id,
                          name: folder.name,
                          currentFolderId: folder.parentFolderId,
                        })
                      }
                    >
                      Move
                    </button>
                    <button
                      type="button"
                      className="drive-action"
                      onClick={() => {
                        setArchiveTarget({ kind: 'folder', id: folder.id, name: folder.name });
                        setModalError(null);
                      }}
                    >
                      Archive
                    </button>
                  </>
                ) : (
                  <span className="drive-cell-muted">—</span>
                )}
              </div>
            </td>
          </tr>
        ))}
        {data.files.map((file: DriveFile) => (
          <tr key={`file-${file.id}`}>
            <td>
              {/* Clicking the name opens what can be opened, and downloads the rest. */}
              <button
                type="button"
                className="drive-name-button"
                onClick={() => (file.previewKind ? setPreviewFile(file) : void download(file.id))}
              >
                <FileIcon />
                <span className="drive-name-text">{file.name}</span>
              </button>
            </td>
            <td className="drive-cell-muted">{file.uploadedBy ? `Added by ${file.uploadedBy}` : 'File'}</td>
            <td className="drive-cell-muted">{formatBytes(file.sizeBytes)}</td>
            <td className="drive-cell-muted">{formatWhen(file.updatedAt)}</td>
            <td>
              <div className="drive-row-actions">
                {file.previewKind ? (
                  <button type="button" className="drive-action" onClick={() => setPreviewFile(file)}>
                    View
                  </button>
                ) : null}
                <button type="button" className="drive-action" onClick={() => void download(file.id)}>
                  Download
                </button>
                {canActOnFile(file) ? (
                  <>
                    <button
                      type="button"
                      className="drive-action"
                      onClick={() => {
                        setRenameTarget({ kind: 'file', id: file.id, name: file.name });
                        setRenameValue(file.name);
                        setModalError(null);
                      }}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="drive-action"
                      onClick={() =>
                        void openMove({ kind: 'file', id: file.id, name: file.name, currentFolderId: file.folderId })
                      }
                    >
                      Move
                    </button>
                    <button
                      type="button"
                      className="drive-action"
                      onClick={() => {
                        setArchiveTarget({ kind: 'file', id: file.id, name: file.name });
                        setModalError(null);
                      }}
                    >
                      Archive
                    </button>
                  </>
                ) : null}
              </div>
            </td>
          </tr>
        ))}
      </>
    );
  };

  const renderSharedRoot = () => (
    <>
      <h3 className="drive-section-heading">Shared with you</h3>
      <table className="drive-table">
        <thead>
          <tr>
            <th>Folder</th>
            <th>Owner</th>
            <th>Access</th>
            <th>Shared</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sharedFolders.length === 0 ? (
            <tr>
              <td colSpan={5} className="drive-empty-cell">
                No one has shared a folder with you yet.
              </td>
            </tr>
          ) : (
            sharedFolders.map((folder: SharedFolder) => (
              <tr key={folder.id}>
                <td>
                  <button type="button" className="drive-name-button" onClick={() => openFolder(folder.id)}>
                    <FolderIcon />
                    <span className="drive-name-text">{folder.name}</span>
                  </button>
                </td>
                <td className="drive-cell-muted">{folder.ownerUsername}</td>
                <td className="drive-cell-muted">
                  {folder.permission === 'edit' ? 'Can edit' : 'Can view'}
                  {folder.via === 'role' && folder.role ? (
                    <span className="drive-pill">via {roleLabel(folder.role)}</span>
                  ) : null}
                </td>
                <td className="drive-cell-muted">{formatWhen(folder.sharedAt)}</td>
                <td>
                  <div className="drive-row-actions">
                    <button type="button" className="drive-action" onClick={() => openFolder(folder.id)}>
                      Open
                    </button>
                    {folder.via === 'user' ? (
                      <button
                        type="button"
                        className="drive-action drive-action--danger"
                        onClick={() => void leaveSharedFolder(folder.id)}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <h3 className="drive-section-heading">Shared by you</h3>
      <table className="drive-table">
        <thead>
          <tr>
            <th>Folder</th>
            <th>People</th>
            <th>Roles</th>
            <th>Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sharedByMe.length === 0 ? (
            <tr>
              <td colSpan={5} className="drive-empty-cell">
                You haven't shared any of your folders yet. Use New shared folder above, or open a folder in Personal and
                use Edit access to turn it into a shared folder.
              </td>
            </tr>
          ) : (
            sharedByMe.map((folder: SharedByMeFolder) => (
              <tr key={folder.id}>
                <td>
                  <button type="button" className="drive-name-button" onClick={() => openFolder(folder.id)}>
                    <FolderIcon />
                    <span className="drive-name-text">{folder.name}</span>
                  </button>
                </td>
                <td className="drive-cell-muted">
                  {folder.userShareCount === 0
                    ? '—'
                    : `${folder.userShareCount} ${folder.userShareCount === 1 ? 'person' : 'people'}`}
                </td>
                <td className="drive-cell-muted">
                  {folder.roles.length === 0 ? '—' : folder.roles.map(roleLabel).join(', ')}
                </td>
                <td className="drive-cell-muted">{formatWhen(folder.updatedAt)}</td>
                <td>
                  <div className="drive-row-actions">
                    <button
                      type="button"
                      className="drive-action"
                      onClick={() => void openShare({ id: folder.id, name: folder.name })}
                    >
                      Manage sharing
                    </button>
                    <button
                      type="button"
                      className="drive-action drive-action--danger"
                      onClick={() => void makeFolderPrivate(folder.id, folder.name)}
                    >
                      Make private
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );

  const renderArchived = () => (
    <>
      <h3 className="drive-section-heading">Archived</h3>
      <p className="drive-section-note">
        Archived files and folders are out of the way but nothing is thrown out. Restore one and it goes back exactly
        where it came from.
      </p>
      <table className="drive-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Kind</th>
            <th>Size</th>
            <th>Was in</th>
            <th>Archived</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {archivedItems.length === 0 ? (
            <tr>
              <td colSpan={6} className="drive-empty-cell">
                Nothing is archived. Use Archive on a file or folder to move it here.
              </td>
            </tr>
          ) : (
            archivedItems.map((item: ArchivedDriveItem) => (
              <tr key={`${item.kind}-${item.id}`}>
                <td>
                  <span className="drive-name-button drive-name-button--static">
                    {item.kind === 'folder' ? <FolderIcon /> : <FileIcon />}
                    <span className="drive-name-text">{item.name}</span>
                  </span>
                </td>
                <td className="drive-cell-muted">{item.kind === 'folder' ? 'Folder' : 'File'}</td>
                <td className="drive-cell-muted">{item.sizeBytes == null ? '—' : formatBytes(item.sizeBytes)}</td>
                <td className="drive-cell-muted">{item.location ?? 'My Drive'}</td>
                <td className="drive-cell-muted">
                  {formatWhen(item.archivedAt)}
                  {item.archivedBy ? <span className="drive-pill">by {item.archivedBy}</span> : null}
                </td>
                <td>
                  <div className="drive-row-actions">
                    <button
                      type="button"
                      className="drive-action"
                      onClick={() => {
                        setRestoreTarget(item);
                        setModalError(null);
                      }}
                    >
                      Restore
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </>
  );

  const shareableUsernames = shareUsers.filter((u) => !shareSelection.includes(u.id)).map((u) => u.username);
  const shareableRoleLabels = ROLE_OPTIONS.filter(
    (role) => !shareRoleSelection.includes(role) && !existingRoleShares.some((s) => s.role === role)
  ).map(roleLabel);
  const isShared = existingShares.length > 0 || existingRoleShares.length > 0;

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader title="File Drive" subtitle="Your own storage, plus folders your teammates share with you" />
          <div className="page-content">
            {!user ? (
              <p className="page-subtitle">Log in to use the file drive.</p>
            ) : sessionExpired || !token ? (
              <div className="drive-callout">
                <h3>Sign in again to use the File Drive</h3>
                <p>
                  The drive keeps each person's files private, so it needs a fresh sign-in to confirm who you are. Log
                  out and log back in, and everything here will be waiting.
                </p>
              </div>
            ) : (
              <>
                <div className="drive-tabs" role="tablist" aria-label="File drive sections">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'personal'}
                    className={`drive-tab${activeTab === 'personal' ? ' drive-tab--active' : ''}`}
                    onClick={() => selectTab('personal')}
                  >
                    Personal
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'shared'}
                    className={`drive-tab${activeTab === 'shared' ? ' drive-tab--active' : ''}`}
                    onClick={() => selectTab('shared')}
                  >
                    Shared
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'archived'}
                    className={`drive-tab${activeTab === 'archived' ? ' drive-tab--active' : ''}`}
                    onClick={() => selectTab('archived')}
                  >
                    Archived
                  </button>
                </div>

                <div
                  className={`drive-surface${dragActive ? ' drive-surface--dropping' : ''}`}
                  onDragEnter={handleDragEnter}
                  onDragOver={(e) => e.preventDefault()}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => void handleDrop(e)}
                >
                  <div className="drive-toolbar">
                    <nav className="drive-breadcrumb" aria-label="Folder path">
                      {/* At the top of a tab there is nowhere to go back to, so the root
                          reads as a label rather than a link. The Shared tab labels its
                          own sections, so it needs nothing here at all. */}
                      {openFolderId == null ? (
                        activeTab === 'personal' ? (
                          <span className="drive-crumb drive-crumb--current">My Drive</span>
                        ) : null
                      ) : (
                        <button type="button" className="drive-crumb" onClick={() => openFolder(null)}>
                          {activeTab === 'shared' ? 'Shared with me' : 'My Drive'}
                        </button>
                      )}
                      {(listing?.breadcrumb ?? []).map((crumb, index, all) => (
                        <React.Fragment key={crumb.id}>
                          <span className="drive-crumb-separator" aria-hidden="true">
                            /
                          </span>
                          {index === all.length - 1 ? (
                            <span className="drive-crumb drive-crumb--current">{crumb.name}</span>
                          ) : (
                            <button type="button" className="drive-crumb" onClick={() => openFolder(crumb.id)}>
                              {crumb.name}
                            </button>
                          )}
                        </React.Fragment>
                      ))}
                    </nav>

                    <div className="drive-toolbar-actions">
                      {listing?.folder && listing.folder.ownerUserId !== user.id ? (
                        <span className="drive-owner-note">
                          Owned by {listing.folder.ownerUsername} · {permission === 'edit' ? 'can edit' : 'view only'}
                        </span>
                      ) : null}
                      {activeTab === 'shared' && openFolderId == null ? (
                        <button
                          type="button"
                          className="drive-button drive-button--primary"
                          onClick={() => {
                            setNewFolderName('');
                            setShareAfterCreate(true);
                            setModalError(null);
                            setNewFolderOpen(true);
                          }}
                        >
                          New shared folder
                        </button>
                      ) : null}
                      {listing?.folder && permission === 'owner' ? (
                        <button
                          type="button"
                          className="drive-button"
                          onClick={() =>
                            void openShare({ id: listing.folder!.id, name: listing.folder!.name })
                          }
                        >
                          Edit access
                        </button>
                      ) : null}
                      {canEdit ? (
                        <>
                          <button
                            type="button"
                            className="drive-button"
                            onClick={() => {
                              setNewFolderName('');
                              setShareAfterCreate(false);
                              setModalError(null);
                              setNewFolderOpen(true);
                            }}
                          >
                            New folder
                          </button>
                          <button type="button" className="drive-button" onClick={() => fileInputRef.current?.click()}>
                            Upload files
                          </button>
                          <button
                            type="button"
                            className="drive-button drive-button--primary"
                            onClick={() => folderInputRef.current?.click()}
                          >
                            Upload folder
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="drive-hidden-input"
                    onChange={(e) => {
                      void handleFilesPicked(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  <input
                    ref={folderInputRef}
                    type="file"
                    multiple
                    className="drive-hidden-input"
                    onChange={(e) => {
                      void handleFilesPicked(e.target.files);
                      e.target.value = '';
                    }}
                  />

                  {notice ? (
                    <div className="drive-notice">
                      <span>{notice}</span>
                      <button type="button" className="drive-notice-close" onClick={() => setNotice(null)} aria-label="Dismiss">
                        ×
                      </button>
                    </div>
                  ) : null}
                  {error ? <div className="drive-error">{error}</div> : null}

                  {loading ? (
                    <p className="drive-loading">Loading…</p>
                  ) : activeTab === 'archived' ? (
                    renderArchived()
                  ) : activeTab === 'shared' && openFolderId == null ? (
                    renderSharedRoot()
                  ) : listing ? (
                    <table className="drive-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Kind</th>
                          <th>Size</th>
                          <th>Modified</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>{renderRows(listing)}</tbody>
                    </table>
                  ) : null}

                  {canEdit ? (
                    <p className="drive-drop-hint">
                      Drag files or whole folders from your computer anywhere onto this page to upload them.
                    </p>
                  ) : null}

                  {dragActive ? <div className="drive-drop-overlay">Drop to upload here</div> : null}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {uploads.length > 0 ? (
        <div className={`drive-uploads${uploadsCollapsed ? ' drive-uploads--collapsed' : ''}`}>
          <div className="drive-uploads-header">
            <span className="drive-uploads-title">
              {activeUploads.length > 0
                ? `Uploading ${uploads.length - activeUploads.length + 1} of ${uploads.length}`
                : `${uploads.filter((u) => u.status === 'done').length} of ${uploads.length} uploaded`}
            </span>
            <div className="drive-uploads-header-actions">
              <button
                type="button"
                className="drive-uploads-icon"
                onClick={() => setUploadsCollapsed((prev) => !prev)}
                aria-label={uploadsCollapsed ? 'Expand uploads' : 'Collapse uploads'}
              >
                {uploadsCollapsed ? '▴' : '▾'}
              </button>
              {activeUploads.length === 0 ? (
                <button
                  type="button"
                  className="drive-uploads-icon"
                  onClick={() => setUploads([])}
                  aria-label="Dismiss uploads"
                >
                  ×
                </button>
              ) : null}
            </div>
          </div>
          <div className="drive-uploads-overall">
            <div className="drive-progress">
              <div className="drive-progress-bar" style={{ width: `${uploadTotals.percent}%` }} />
            </div>
            <span className="drive-uploads-overall-label">
              {formatBytes(uploadTotals.loaded)} of {formatBytes(uploadTotals.total)}
            </span>
          </div>
          {!uploadsCollapsed ? (
            <ul className="drive-uploads-list">
              {uploads.map((item) => {
                const percent = item.status === 'done' ? 100 : item.size > 0 ? Math.round((item.loaded / item.size) * 100) : 0;
                return (
                  <li key={item.id} className="drive-upload-row">
                    <div className="drive-upload-line">
                      <span className="drive-upload-name" title={item.name}>
                        {item.name}
                      </span>
                      {item.status === 'queued' || item.status === 'uploading' ? (
                        <button
                          type="button"
                          className="drive-upload-cancel"
                          onClick={() => cancelUpload(item.id)}
                          aria-label={`Cancel upload of ${item.name}`}
                        >
                          ×
                        </button>
                      ) : (
                        <span
                          className={`drive-upload-status drive-upload-status--${item.status}`}
                        >
                          {item.status === 'done' ? 'Done' : item.status === 'cancelled' ? 'Cancelled' : 'Failed'}
                        </span>
                      )}
                    </div>
                    <div className="drive-progress drive-progress--slim">
                      <div
                        className={`drive-progress-bar${item.status === 'error' ? ' drive-progress-bar--error' : ''}`}
                        style={{ width: `${item.status === 'error' || item.status === 'cancelled' ? 100 : percent}%` }}
                      />
                    </div>
                    <span className="drive-upload-meta">
                      {item.status === 'error'
                        ? item.error
                        : item.status === 'queued'
                          ? 'Waiting…'
                          : `${formatBytes(item.loaded)} of ${formatBytes(item.size)}`}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}

      {previewFile ? (
        <DriveFilePreview
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          onDownload={(file) => void download(file.id)}
          onError={handleApiError}
        />
      ) : null}

      <Modal
        isOpen={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        title={shareAfterCreate ? 'New shared folder' : 'New folder'}
        narrow
      >
        <div className="modal-body">
          <label className="drive-field-label" htmlFor="drive-new-folder">
            Folder name
          </label>
          <input
            id="drive-new-folder"
            className="drive-input"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submitNewFolder();
            }}
            placeholder="e.g. Service reports"
            autoFocus
          />
          {shareAfterCreate ? (
            <p className="drive-modal-hint">
              The folder is created in your drive and you pick who can see it next. It shows up under Shared by you once
              you add people or roles.
            </p>
          ) : null}
          {modalError ? <p className="drive-modal-error">{modalError}</p> : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setNewFolderOpen(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="drive-button drive-button--primary"
            disabled={busy || !newFolderName.trim()}
            onClick={() => void submitNewFolder()}
          >
            {busy ? 'Creating…' : shareAfterCreate ? 'Create and share' : 'Create folder'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={renameTarget != null}
        onClose={() => setRenameTarget(null)}
        title={renameTarget?.kind === 'folder' ? 'Rename folder' : 'Rename file'}
        narrow
      >
        <div className="modal-body">
          <label className="drive-field-label" htmlFor="drive-rename">
            New name
          </label>
          <input
            id="drive-rename"
            className="drive-input"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submitRename();
            }}
            autoFocus
          />
          {modalError ? <p className="drive-modal-error">{modalError}</p> : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setRenameTarget(null)}>
            Cancel
          </button>
          <button
            type="button"
            className="drive-button drive-button--primary"
            disabled={busy || !renameValue.trim()}
            onClick={() => void submitRename()}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={moveTarget != null}
        onClose={() => setMoveTarget(null)}
        title={moveTarget?.kind === 'folder' ? 'Move folder' : 'Move file'}
        narrow
      >
        <div className="modal-body">
          <p className="drive-modal-text">
            Where should <strong>{moveTarget?.name}</strong> go?
          </p>
          <SearchableDropdown
            options={moveChoices.map((c) => c.label)}
            value={moveDestination}
            onChange={setMoveDestination}
            placeholder="Search folders…"
          />
          {modalError ? <p className="drive-modal-error">{modalError}</p> : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setMoveTarget(null)}>
            Cancel
          </button>
          <button
            type="button"
            className="drive-button drive-button--primary"
            disabled={busy || !moveDestination}
            onClick={() => void submitMove()}
          >
            {busy ? 'Moving…' : 'Move'}
          </button>
        </div>
      </Modal>

      <Modal isOpen={archiveTarget != null} onClose={() => setArchiveTarget(null)} title="Archive" narrow>
        <div className="modal-body">
          <p className="drive-modal-text">
            {archiveTarget?.kind === 'folder' ? (
              <>
                Archive <strong>{archiveTarget?.name}</strong> and everything inside it? It moves to the Archived tab,
                where you can restore it.
              </>
            ) : (
              <>
                Archive <strong>{archiveTarget?.name}</strong>? It moves to the Archived tab, where you can restore it.
              </>
            )}
          </p>
          {modalError ? <p className="drive-modal-error">{modalError}</p> : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setArchiveTarget(null)}>
            Cancel
          </button>
          <button type="button" className="drive-button drive-button--primary" disabled={busy} onClick={() => void submitArchive()}>
            {busy ? 'Archiving…' : 'Archive'}
          </button>
        </div>
      </Modal>

      <Modal isOpen={restoreTarget != null} onClose={() => setRestoreTarget(null)} title="Restore" narrow>
        <div className="modal-body">
          <p className="drive-modal-text">
            {restoreTarget?.kind === 'folder' ? (
              <>
                Restore <strong>{restoreTarget?.name}</strong> and its contents to{' '}
                <strong>{restoreTarget?.location ?? 'My Drive'}</strong>?
              </>
            ) : (
              <>
                Restore <strong>{restoreTarget?.name}</strong> to{' '}
                <strong>{restoreTarget?.location ?? 'My Drive'}</strong>?
              </>
            )}
          </p>
          {modalError ? <p className="drive-modal-error">{modalError}</p> : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setRestoreTarget(null)}>
            Cancel
          </button>
          <button type="button" className="drive-button drive-button--primary" disabled={busy} onClick={() => void submitRestore()}>
            {busy ? 'Restoring…' : 'Restore'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={shareTarget != null}
        onClose={() => setShareTarget(null)}
        title={`Share “${shareTarget?.name ?? ''}”`}
        narrow
      >
        <div className="modal-body">
          <p className="drive-modal-text">
            Everyone you add gets an email with a link, and can see everything inside this folder. You can come back and
            add more people or roles at any time.
          </p>

          <label className="drive-field-label">Add people</label>
          <div className="drive-chips">
            {shareSelection.length === 0 ? (
              <span className="drive-chips-empty">No one selected yet</span>
            ) : (
              shareSelection.map((id) => {
                const picked = shareUsers.find((u) => u.id === id);
                if (!picked) return null;
                return (
                  <span key={id} className="drive-chip">
                    {picked.username}
                    <button
                      type="button"
                      className="drive-chip-remove"
                      onClick={() => setShareSelection((prev) => prev.filter((x) => x !== id))}
                      aria-label={`Remove ${picked.username}`}
                    >
                      ×
                    </button>
                  </span>
                );
              })
            )}
          </div>
          <SearchableDropdown
            options={shareableUsernames}
            value={shareAddField}
            onChange={(value) => {
              setShareAddField(value);
              const match = shareUsers.find((u) => u.username === value.trim());
              if (match) {
                setShareSelection((prev) => (prev.includes(match.id) ? prev : [...prev, match.id]));
                setShareAddField('');
              }
            }}
            placeholder={shareableUsernames.length === 0 ? 'Everyone added' : 'Search a teammate…'}
          />

          <label className="drive-field-label">Add roles</label>
          <div className="drive-chips">
            {shareRoleSelection.length === 0 ? (
              <span className="drive-chips-empty">No roles selected</span>
            ) : (
              shareRoleSelection.map((role) => (
                <span key={role} className="drive-chip">
                  {roleLabel(role)}
                  <button
                    type="button"
                    className="drive-chip-remove"
                    onClick={() => setShareRoleSelection((prev) => prev.filter((r) => r !== role))}
                    aria-label={`Remove ${roleLabel(role)}`}
                  >
                    ×
                  </button>
                </span>
              ))
            )}
          </div>
          <SearchableDropdown
            options={shareableRoleLabels}
            value={shareRoleField}
            onChange={(value) => {
              setShareRoleField(value);
              const match = ROLE_OPTIONS.find((role) => roleLabel(role) === value.trim());
              if (match) {
                setShareRoleSelection((prev) => (prev.includes(match) ? prev : [...prev, match]));
                setShareRoleField('');
              }
            }}
            placeholder={shareableRoleLabels.length === 0 ? 'All roles added' : 'Search a role…'}
          />
          <p className="drive-modal-hint">
            Sharing with a role covers everyone who holds it, including people who get the role later.
          </p>

          <label className="drive-field-label" htmlFor="drive-share-permission">
            They can
          </label>
          <select
            id="drive-share-permission"
            className="drive-input"
            value={sharePermission}
            onChange={(e) => setSharePermission(e.target.value === 'edit' ? 'edit' : 'view')}
          >
            <option value="view">View and download</option>
            <option value="edit">View, download and add files</option>
          </select>

          {isShared ? (
            <div className="drive-share-list">
              <span className="drive-field-label">Already shared with</span>
              {existingShares.map((share) => (
                <div key={`user-${share.userId}`} className="drive-share-row">
                  <span className="drive-share-name">
                    {share.username}
                    <span className="drive-share-email">{share.email}</span>
                  </span>
                  <select
                    className="drive-share-select"
                    value={share.permission}
                    onChange={(e) => void changeSharePermission(share.userId, e.target.value === 'edit' ? 'edit' : 'view')}
                    aria-label={`Access for ${share.username}`}
                  >
                    <option value="view">Can view</option>
                    <option value="edit">Can edit</option>
                  </select>
                  <button
                    type="button"
                    className="drive-action drive-action--danger"
                    onClick={() => void revokeShare(share.userId)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {existingRoleShares.map((share) => (
                <div key={`role-${share.role}`} className="drive-share-row">
                  <span className="drive-share-name">
                    {roleLabel(share.role)} role
                    <span className="drive-share-email">
                      {share.memberCount} {share.memberCount === 1 ? 'person holds it' : 'people hold it'}
                    </span>
                  </span>
                  <select
                    className="drive-share-select"
                    value={share.permission}
                    onChange={(e) =>
                      void changeRoleSharePermission(share.role, e.target.value === 'edit' ? 'edit' : 'view')
                    }
                    aria-label={`Access for the ${roleLabel(share.role)} role`}
                  >
                    <option value="view">Can view</option>
                    <option value="edit">Can edit</option>
                  </select>
                  <button
                    type="button"
                    className="drive-action drive-action--danger"
                    onClick={() => void revokeRoleShare(share.role)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="drive-action drive-action--danger drive-make-private"
                disabled={busy}
                onClick={() => shareTarget && void makeFolderPrivate(shareTarget.id, shareTarget.name)}
              >
                Stop sharing with everyone
              </button>
            </div>
          ) : null}

          {modalError ? <p className="drive-modal-error">{modalError}</p> : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setShareTarget(null)}>
            Done
          </button>
          <button
            type="button"
            className="drive-button drive-button--primary"
            disabled={busy || (shareSelection.length === 0 && shareRoleSelection.length === 0)}
            onClick={() => void submitShare()}
          >
            {busy ? 'Sharing…' : 'Share and notify'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default FileDrive;
